import React, { useState, useEffect, useRef } from 'react';
import QRCode from "react-qr-code";

// Coin icons
import usdtIcon from '../images/coin/usdt.svg';
import usdcIcon from '../images/coin/usdc.svg';
import bnbIcon from '../images/coin/bnb.svg';
import busdIcon from '../images/coin/busd.svg';
import solIcon from '../images/coin/sol.svg';
import ethIcon from '../images/coin/eth.svg';
// Chain icons
import bscIcon from '../images/chain/bsc.svg';
import ethChainIcon from '../images/chain/ethereum.svg';
import solChainIcon from '../images/chain/solana.svg';

import './Recharge.css';

const Recharge = () => {
    // Recharge state
    const coins = [
        { value: 'USDT', label: 'USDT', icon: usdtIcon },
        { value: 'USDC', label: 'USDC', icon: usdcIcon },
        { value: 'BNB', label: 'BNB', icon: bnbIcon },
        { value: 'BUSD', label: 'BUSD', icon: busdIcon },
        { value: 'SOL', label: 'SOL', icon: solIcon },
        { value: 'ETH', label: 'ETH', icon: ethIcon },
    ];

    const chains = [
        { value: 'BSC', label: 'BSC', icon: bscIcon },
        { value: 'Ethereum', label: 'Ethereum', icon: ethChainIcon },
        { value: 'Solana', label: 'Solana', icon: solChainIcon },
    ];

    // Deposit addresses mapping
    const depositAddresses = {
        'BSC': '0xb272001e66Df0Eea02153FBA2c77Fd499576045e',
        'Ethereum': '0x71C7656EC7ab88b098defB751B7401B5f6d8976F',
        'Solana': '5U6wFG8e4p9f1F1a2a1b1c1d1e1f1g1h1i1j1k1l1m'
    };

    const [selectedCoin, setSelectedCoin] = useState(coins[0]);
    const [selectedChain, setSelectedChain] = useState(chains[0]);
    const [showCoinDropdown, setShowCoinDropdown] = useState(false);
    const [showChainDropdown, setShowChainDropdown] = useState(false);
    const [depositAddress, setDepositAddress] = useState(depositAddresses['BSC']);
    const [showCopyToast, setShowCopyToast] = useState(false);

    const coinRef = useRef(null);
    const chainRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (coinRef.current && !coinRef.current.contains(event.target)) {
                setShowCoinDropdown(false);
            }
            if (chainRef.current && !chainRef.current.contains(event.target)) {
                setShowChainDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        // Update deposit address when chain changes
        // Also could depend on token if addresses differ by token on same chain
        setDepositAddress(depositAddresses[selectedChain.value] || depositAddresses['BSC']);
    }, [selectedChain]);

    const copyToClipboard = () => {
        navigator.clipboard.writeText(depositAddress).then(() => {
            setShowCopyToast(true);
            setTimeout(() => {
                setShowCopyToast(false);
            }, 3000);
        }, (err) => {
            console.error('Could not copy text: ', err);
        });
    };

    return (
        <>
            <div className="recharge-card">
                <div className="recharge-header-text">
                    <h2>Transfer Crypto</h2>
                    <p className="balance-text">Polymarket Balance: $0.01</p>
                </div>

                <div className="selection-row">
                    <div className="select-group">
                        <label>Supported token</label>
                        <div className="custom-select" ref={coinRef} onClick={() => setShowCoinDropdown(!showCoinDropdown)}>
                            <div className="selected-option">
                                <img src={selectedCoin.icon} alt={selectedCoin.label} />
                                <span>{selectedCoin.label}</span>
                            </div>
                            <span className="arrow-down">▼</span>
                            {showCoinDropdown && (
                                <div className="options-list">
                                    {coins.map(coin => (
                                        <div
                                            key={coin.value}
                                            className="option-item"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectedCoin(coin);
                                                setShowCoinDropdown(false);
                                            }}
                                        >
                                            <img src={coin.icon} alt={coin.label} />
                                            <span>{coin.label}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="select-group">
                        <label>Supported chain <span className="info-text">Min $20 ⓘ</span></label>
                        <div className="custom-select" ref={chainRef} onClick={() => setShowChainDropdown(!showChainDropdown)}>
                            <div className="selected-option">
                                <img src={selectedChain.icon} alt={selectedChain.label} />
                                <span>{selectedChain.label}</span>
                            </div>
                            <span className="arrow-down">▼</span>
                            {showChainDropdown && (
                                <div className="options-list">
                                    {chains.map(chain => (
                                        <div
                                            key={chain.value}
                                            className="option-item"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectedChain(chain);
                                                setShowChainDropdown(false);
                                            }}
                                        >
                                            <img src={chain.icon} alt={chain.label} />
                                            <span>{chain.label}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="qr-section">
                    <div className="qr-container">
                        <div className="qr-wrapper">
                            <QRCode
                                value={depositAddress}
                                size={160}
                                style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                                viewBox={`0 0 256 256`}
                            />
                        </div>
                    </div>
                </div>

                <div className="address-section">
                    <div className="address-header">
                        <span className="address-label">Your deposit address ⓘ</span>
                        <a href="#" className="terms-link">Terms apply</a>
                    </div>
                    <div className="address-display">
                        {depositAddress}
                    </div>
                    <button className="copy-btn" onClick={copyToClipboard}>
                        <svg viewBox="0 0 24 24" width="16" height="16"><path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" /></svg>
                        Copy address
                    </button>
                </div>

                <div className="recharge-footer">
                    <div className="network-warning">
                        Only send {selectedCoin.label} to this address on the {selectedChain.label} network.
                    </div>
                </div>
            </div>

            {showCopyToast && (
                <div className="copy-toast">
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="#4caf50">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                    </svg>
                    Address copied to clipboard!
                </div>
            )}
        </>
    );
};

export default Recharge;